import { useCallback } from 'react'
import { Button } from '@/components/ui/button'
import { Icon } from '@/components/ui/icon'
import { useStudio } from '@/components/studio/StudioContext'
import { CollapsibleSection, SelectParam } from './settings-sections'

export interface FrameInputNodeSettingsProps {
  nodeId: string
  state: Record<string, unknown>
  onStateChange: (updater: (prev: Record<string, unknown>) => Record<string, unknown>) => void
}

/**
 * Configuration for a Frame Input node.
 *
 * The node itself stays image-first — it shows the pinned frame and nothing
 * else. Choosing which frame and which version lives here, because a Frame
 * Input is a pointer into Artboard rather than a stored file: the frame keeps
 * gaining versions after the node is placed, and the node stays pinned to the
 * one that was chosen.
 */
export function FrameInputNodeSettings({ state, onStateChange }: FrameInputNodeSettingsProps) {
  const studio = useStudio()

  const frames = studio.project?.artboards ?? []
  // Only frames with a stored asset can be used as an input.
  const usableFrames = frames.filter(f => f.versions.some(v => v.assetId))
  const frame = frames.find(f => f.id === state.frameId)
  const version = frame?.versions.find(v => v.id === state.versionId)
  const stale = Boolean(frame && version && frame.versions[frame.activeVersion]?.id !== version.id)

  const handleFrameChange = useCallback((frameId: string) => {
    const next = frames.find(f => f.id === frameId)
    onStateChange(prev => ({
      ...prev,
      frameId: next?.id ?? '',
      versionId: next?.versions[next.activeVersion]?.id ?? '',
      images: [],
    }))
  }, [frames, onStateChange])

  const handleVersionChange = useCallback((versionId: string) => {
    onStateChange(prev => ({ ...prev, versionId, images: [] }))
  }, [onStateChange])

  const pinToLatest = useCallback(() => {
    if (!frame) return
    const latest = frame.versions[frame.activeVersion]
    if (latest) handleVersionChange(latest.id)
  }, [frame, handleVersionChange])

  return (
    <CollapsibleSection title="Source frame">
      <div className="flex flex-col gap-3 py-2">
        <SelectParam
          label="Artboard frame"
          value={frame?.id ?? ''}
          onChange={handleFrameChange}
          placeholder={usableFrames.length ? 'Choose an image frame' : 'No image frames yet'}
          options={usableFrames.map(f => ({ value: f.id, label: f.title }))}
        />

        <SelectParam
          label="Use version"
          value={version?.id ?? ''}
          onChange={handleVersionChange}
          placeholder={frame ? 'Choose a version' : 'Choose a frame first'}
          options={(frame?.versions ?? []).map((v, i) => ({
            value: v.id,
            label: `v${i + 1} · ${v.label}`,
          }))}
        />

        {stale && (
          <div role="status" className="mx-2 flex gap-2 rounded-md bg-amber-500/10 p-2 text-xs text-amber-700 dark:text-amber-300">
            <Icon name="info" className="mt-px h-3.5 w-3.5 shrink-0" />
            <div className="flex flex-col items-start gap-1.5">
              <span>Artboard has moved on to a newer version. This input stays pinned to the one you chose.</span>
              <button type="button" onClick={pinToLatest} className="font-medium underline underline-offset-2">
                Pin to the latest version
              </button>
            </div>
          </div>
        )}

        <div className="px-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="w-full"
            disabled={!frame}
            onClick={() => frame && studio.showFrame(frame.id)}
          >
            <Icon name="frame" className="mr-2 h-4 w-4" />
            Show in Artboard
          </Button>
        </div>
      </div>
    </CollapsibleSection>
  )
}
